import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { ArrowLeft, Calendar, Clock, Edit2, Home, MapPin, Plane, Trash2, Users, X } from 'lucide-react';
import { EventAttendancePanel } from '../attendance/EventAttendancePanel';
import { toast } from 'react-hot-toast';
import { useTeamStore } from '../../store/teamStore';
import { gameService, gameResult, type Game } from '../../services/gameService';
import { supabase } from '../../lib/supabase';
import { playerCardService } from '../../services/playerCardService';
import { AddGameStatsModal } from '../player-cards/AddGameStatsModal';
import { FootballGameStatsModal } from '../player-cards/FootballGameStatsModal';
import { ConfirmModal } from '../ConfirmModal';
import { getCalculatedStats } from '../../utils/statCalculations';
import { GenericSportGameDetail } from './GenericSportGameDetail';
import { FootballGameDetail } from './football/FootballGameDetail';
import { BaseballGameDetail } from './baseball/BaseballGameDetail';
import { TennisMatchStatusCard } from './tennis/TennisMatchStatusCard';
import { TennisMatchDetail } from './tennis/TennisMatchDetail';
import { getEventLabel } from '../../data/sportRulesets';
import type { Player, StatWithId } from '../../types';
import type { DepthChartEntry } from '../../types/depth-chart';

interface GameDetailViewProps {}

export const GameDetailView: React.FC<GameDetailViewProps> = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentTeam, fetchAllTeams } = useTeamStore();

  const [game, setGame] = useState<Game | null>(null);
  /** Restoring selected team after refresh (/games/:id); without fetch, currentTeam stays null forever. */
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lineup, setLineup] = useState<Array<{ order: number; player: Player | null }>>([]);
  const [playerStats, setPlayerStats] = useState<Map<string, StatWithId[]>>(new Map());
  const [calculatedStats, setCalculatedStats] = useState<Map<string, Record<string, number>>>(new Map());
  const [showAddStatsModal, setShowAddStatsModal] = useState(false);
  const [selectedPlayerCardId, setSelectedPlayerCardId] = useState<string | null>(null);
  const [playerCards, setPlayerCards] = useState<Map<string, string>>(new Map()); // player_id -> player_card_id
  const [allTeamPlayers, setAllTeamPlayers] = useState<Player[]>([]);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [showAttendance, setShowAttendance] = useState(false);
  const [expandedStatsPlayerIds, setExpandedStatsPlayerIds] = useState<Set<string>>(new Set());
  const [showEditGameModal, setShowEditGameModal] = useState(false);
  const [editGameSaving, setEditGameSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingGame, setDeletingGame] = useState(false);
  const [editGameForm, setEditGameForm] = useState({
    date: '',
    opponent_name: '',
    home_away: 'home' as 'home' | 'away',
    location: '',
    game_time: '',
  });
  const [showScoreOverride, setShowScoreOverride] = useState(false);
  const [overrideOurScore, setOverrideOurScore] = useState('');
  const [overrideOpponentScore, setOverrideOpponentScore] = useState('');
  const [savingScoreOverride, setSavingScoreOverride] = useState(false);

  // Scores are derived from loaded stats (R = our runs, R_Allowed = opponent runs)
  // and auto-persisted to the DB so the game list can display them.
  const derivedScores = useMemo(() => {
    if (playerStats.size === 0) return null;
    let ourRuns = 0;
    let opponentRuns = 0;
    for (const stats of playerStats.values()) {
      for (const { stat_key, stat_value } of stats) {
        if (!Number.isFinite(stat_value)) continue;
        if (stat_key === 'R') ourRuns += stat_value;
        if (stat_key === 'R_Allowed') opponentRuns += stat_value;
      }
    }
    return { ourRuns, opponentRuns };
  }, [playerStats]);

  // Auto-persist derived score back to the DB whenever it changes (baseball/softball only).
  // Football scores are set explicitly via handleSaveScore / Hudl import. Skipped once
  // manual_score is set — e.g. a personal account's one-player stats can't capture the
  // final score of the game, so a manual override takes over from here.
  useEffect(() => {
    const sport = currentTeam?.sport;
    if (!gameId || !derivedScores || game?.manual_score || (sport !== 'Baseball' && sport !== 'Softball')) return;
    void gameService.saveScore(gameId, derivedScores.ourRuns, derivedScores.opponentRuns);
  }, [gameId, derivedScores, currentTeam?.sport, game?.manual_score]);

  // What the scoreboard actually shows: the manual override once one is set, otherwise the live stats-derived total.
  const displayScores = game?.manual_score
    ? { ourRuns: game?.our_score ?? 0, opponentRuns: game?.opponent_score ?? 0 }
    : derivedScores;

  const handleOpenScoreOverride = () => {
    setOverrideOurScore(String(displayScores?.ourRuns ?? 0));
    setOverrideOpponentScore(String(displayScores?.opponentRuns ?? 0));
    setShowScoreOverride(true);
  };

  const handleSaveScoreOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameId) return;
    const our = parseInt(overrideOurScore, 10);
    const opponent = parseInt(overrideOpponentScore, 10);
    if (!Number.isFinite(our) || !Number.isFinite(opponent) || our < 0 || opponent < 0) {
      toast.error('Enter a valid score for both teams');
      return;
    }
    setSavingScoreOverride(true);
    try {
      await gameService.setManualScore(gameId, our, opponent);
      await refreshGame();
      setShowScoreOverride(false);
      toast.success('Score updated');
    } catch {
      toast.error('Failed to update score');
    } finally {
      setSavingScoreOverride(false);
    }
  };

  const handleUseStatsDerivedScore = async () => {
    if (!gameId) return;
    setSavingScoreOverride(true);
    try {
      await gameService.clearManualScore(gameId);
      await refreshGame();
      toast.success('Switched back to the stats-derived score');
    } catch {
      toast.error('Failed to update score');
    } finally {
      setSavingScoreOverride(false);
    }
  };

  // Pitcher-load indicator: sum of `Pitches` stat rows for each player in this
  // game, surfaced as a small chip on the fielding view so coaches can see
  // Baseball/softball fielding assignments are stored per-game (like batting order)
  const isBaseballLike = currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball';
  const isFootball = currentTeam?.sport === 'Football' || currentTeam?.sport === 'FlagFootball';
  const isTennis = currentTeam?.sport === 'Tennis';
  const eventLabel = getEventLabel(currentTeam?.sport);

  // On hard refresh the store is empty — fetch teams so currentTeam gets populated.
  useEffect(() => {
    if (!currentTeam) {
      fetchAllTeams();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTeamsLoading(true);
      await fetchAllTeams();
      if (!cancelled) setTeamsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAllTeams]);

  useEffect(() => {
    if (!gameId || teamsLoading) return;
    if (!currentTeam?.id) {
      setLoading(false);
      return;
    }
    void loadGame();
    void loadPlayerCards();
    void loadAllTeamPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load helpers close over fresh team/gameId via effect re-runs when team appears
  }, [gameId, currentTeam?.id, teamsLoading]);

  // Reload lineup/fielding whenever the Coach agent (or any other source)
  // saves changes to this game outside the normal in-page save flow.
  useEffect(() => {
    if (!gameId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ gameId: string }>).detail;
      if (detail?.gameId === gameId) {
        loadGame();
      }
    };
    window.addEventListener('game-lineup-updated', handler);
    return () => window.removeEventListener('game-lineup-updated', handler);
  }, [gameId]);

  useEffect(() => {
    if (game && currentTeam?.id && gameId) {
      loadLineup(game);
    }
  }, [game, currentTeam?.id, gameId]);

  useEffect(() => {
    if (gameId && currentTeam?.id) {
      loadGameStats();
    }
  }, [gameId, currentTeam?.id, lineup]);

  const loadGame = async () => {
    if (!gameId) return;
    
    setLoading(true);
    try {
      const gameData = await gameService.getGame(gameId);
      setGame(gameData);
    } catch (error) {
      console.error('Error loading game:', error);
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const refreshGame = useCallback(async () => {
    if (!gameId) return;
    try {
      const gameData = await gameService.getGame(gameId);
      setGame(gameData);
    } catch (err) {
      console.error('Error refreshing game:', err);
    }
  }, [gameId]);

  const loadAllTeamPlayers = async () => {
    if (!currentTeam?.id) return;
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', currentTeam.id)
        .order('last_name');
      if (error || !data) return;

      // Enrich players with player-card position data so the randomizer can
      // respect each player's eligible_positions and role_types.
      const { data: cardData } = await supabase
        .from('player_cards')
        .select('player_id, eligible_positions, role_types')
        .eq('team_id', currentTeam.id);

      const cardByPlayer = new Map(
        (cardData || []).map((c: { player_id: string; eligible_positions: string[] | null; role_types: string[] | null }) => [c.player_id, c]),
      );

      const enriched = data.map((p: Player) => ({
        ...p,
        eligible_positions: cardByPlayer.get(p.id)?.eligible_positions ?? [],
        role_types: cardByPlayer.get(p.id)?.role_types ?? [],
      }));

      setAllTeamPlayers(enriched);
    } catch (e) {
      console.error('Error loading team players:', e);
    }
  };

  // Lineup source:
  // - manual: use game_lineup (batting order stored per-game)
  // - depth_chart: use the team's default depth chart (unit = 'Lineup', depth = 1)
  const loadLineup = useCallback(async (gameData: Game) => {
    if (!currentTeam?.id || !gameId) return;

    try {
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', currentTeam.id);

      if (playersError) {
        console.error('Error loading players:', playersError);
        return;
      }
      const playersList = playersData || [];

      let lineupEntries: Array<{ order: number; player: Player | null }> = [];

      if (gameData.lineup_source === 'manual') {
        const entries = await gameService.getGameLineup(gameData.id);
        lineupEntries = entries.map((e) => {
          const player = playersList.find((p: Player) => p.id === e.player_id) || null;
          return { order: e.batting_order, player };
        });
      } else {
        const { data: depthChartData, error: depthChartError } = await supabase
          .from('depth_chart')
          .select('*')
          .eq('team_id', currentTeam.id)
          .eq('unit', 'Lineup')
          .eq('depth', 1)
          .order('position', { ascending: true });

        if (depthChartError) {
          console.error('Error loading depth chart lineup:', depthChartError);
          return;
        }

        lineupEntries = (depthChartData || []).map((entry: DepthChartEntry) => {
          const order = parseInt(entry.position, 10) || 1;
          const player = playersList.find((p: Player) => p.id === entry.player_id) || null;
          return { order, player };
        });
      }

      lineupEntries.sort((a, b) => a.order - b.order);
      setLineup(lineupEntries);
    } catch (error) {
      console.error('Error loading lineup:', error);
    }
  }, [currentTeam?.id, gameId]);

  const loadPlayerCards = async () => {
    if (!currentTeam?.id) return;

    try {
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      const cardMap = new Map<string, string>();
      cards.forEach(card => {
        if (card.player?.id) {
          cardMap.set(card.player.id, card.id);
        }
      });
      setPlayerCards(cardMap);
    } catch (error) {
      console.error('Error loading player cards:', error);
    }
  };

  const loadGameStats = async () => {
    if (!gameId || !currentTeam?.id) return;

    try {
      // Load all stats for this game
      const { data: statsData, error } = await supabase
        .from('sport_stats')
        .select(`
          *,
          player_card:player_cards!inner(
            id,
            player:players(id, first_name, last_name, number)
          )
        `)
        .eq('team_id', currentTeam.id)
        .eq('game_id', gameId);

      if (error) {
        console.error('Error loading game stats:', error);
        return;
      }

      // Group stats by player (including stat IDs)
      const statsMap = new Map<string, StatWithId[]>();
      const calculatedMap = new Map<string, Record<string, number>>();

      (statsData || []).forEach((stat: any) => {
        const playerCardId = stat.player_card?.id;
        const playerId = stat.player_card?.player?.id;
        
        if (!playerCardId || !playerId) return;

        if (!statsMap.has(playerId)) {
          statsMap.set(playerId, []);
        }

        statsMap.get(playerId)!.push({
          id: stat.id,
          stat_key: stat.stat_key,
          stat_value: parseFloat(stat.stat_value.toString()),
        });
      });

      // Calculate stats for each player
      statsMap.forEach((stats, playerId) => {
        if (currentTeam?.sport) {
          // Convert to format expected by getCalculatedStats
          const statsForCalc = stats.map(s => ({ stat_key: s.stat_key, stat_value: s.stat_value }));
          calculatedMap.set(playerId, getCalculatedStats(currentTeam.sport, statsForCalc, currentTeam.innings));
        }
      });

      setPlayerStats(statsMap);
      setCalculatedStats(calculatedMap);
    } catch (error) {
      console.error('Error loading game stats:', error);
    }
  };

  const handleStatsAdded = () => {
    loadGameStats();
    setShowAddStatsModal(false);
    setSelectedPlayerCardId(null);
    setEditingPlayerId(null);
  };

  const hasExistingGameStats = [...playerStats.values()].some((arr) => arr.length > 0);

  const openEditGameModal = useCallback(() => {
    if (!game) return;
    const raw = game.date;
    const dateOnly = raw.includes('T') ? raw.split('T')[0]! : raw.slice(0, 10);
    let timeVal = '';
    const gt = game.game_time;
    if (gt) {
      const m = /^(\d{1,2}):(\d{2})/.exec(gt);
      if (m) timeVal = `${m[1].padStart(2, '0')}:${m[2]}`;
    }
    setEditGameForm({
      date: dateOnly,
      opponent_name: game.opponent_name,
      home_away: game.home_away,
      location: game.location ?? '',
      game_time: timeVal,
    });
    setShowEditGameModal(true);
  }, [game]);

  const saveEditedGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameId || !game) return;
    if (!editGameForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!editGameForm.opponent_name.trim()) {
      toast.error('Please enter an opponent name');
      return;
    }
    setEditGameSaving(true);
    try {
      const updated = await gameService.updateGame(gameId, {
        date: editGameForm.date,
        opponent_name: editGameForm.opponent_name.trim(),
        home_away: editGameForm.home_away,
        location: editGameForm.location.trim() || null,
        game_time: editGameForm.game_time.trim() || null,
      });
      setGame(updated);
      toast.success('Game details updated');
      setShowEditGameModal(false);
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to update game');
    } finally {
      setEditGameSaving(false);
    }
  };

  const playersWithCards = allTeamPlayers.filter((p) => playerCards.has(p.id));

  const toggleStatsExpanded = useCallback((playerId: string) => {
    setExpandedStatsPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }, []);

  // Record-stats list order: game lineup first (batting order), then other players (main roster before subs, then by number)
  const recordStatsOrderedPlayers = (() => {
    const lineupIds = new Set(lineup.map(({ player }) => player?.id).filter(Boolean) as string[]);
    const inLineupOrder = lineup
      .map(({ order, player }) => (player && playerCards.has(player.id) ? { order, player } : null))
      .filter((x): x is { order: number; player: Player } => x != null)
      .sort((a, b) => a.order - b.order)
      .map(({ player }) => player);
    const notInLineup = playersWithCards
      .filter((p) => !lineupIds.has(p.id))
      .sort((a, b) => {
        // Main roster (non-subs) before subs, then by jersey number
        const aSub = (a as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
        const bSub = (b as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
        if (aSub !== bSub) return aSub - bSub;
        return (a.number ?? 999) - (b.number ?? 999);
      });
    return [...inLineupOrder, ...notInLineup];
  })();

  const handleDeleteGame = async () => {
    if (!gameId) return;
    setDeletingGame(true);
    try {
      await gameService.deleteGame(gameId);
      toast.success('Game deleted');
      navigate('/games');
    } catch (e: any) {
      console.error('[GameDetailView] delete game error:', e);
      toast.error(e?.message || 'Failed to delete game');
    } finally {
      setDeletingGame(false);
    }
  };

  if (teamsLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-700 mb-2">No team is selected.</p>
        <p className="text-sm text-gray-500 mb-4">Pick your team from Home, then reopen this game from Games.</p>
        <button type="button" onClick={() => navigate('/dashboard')} className="btn-primary">
          Go to Home
        </button>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{eventLabel.singular} not found</p>
        <button
          onClick={() => navigate('/games')}
          className="mt-4 btn-primary"
        >
          Back to {eventLabel.plural}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/games')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {game.opponent_name}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 flex-wrap">
            <span>{new Date(game.date + 'T00:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}</span>
            {game.game_time && (
              <span>{new Date(`2000-01-01T${game.game_time}`).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}</span>
            )}
            {game.location && (
              <span>{game.location}</span>
            )}
            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
              {game.home_away === 'home' ? 'Home' : 'Away'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start">
          {/* Attendance tracks who of a multi-player roster showed up — not meaningful for a personal (parent/player) account's one player. */}
          {!currentTeam?.isPersonal && (
            <button
              type="button"
              onClick={() => setShowAttendance(!showAttendance)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              aria-label="Toggle game attendance"
            >
              <Users size={16} />
              <span className="hidden sm:inline">Attendance</span>
            </button>
          )}
          <button
            type="button"
            onClick={openEditGameModal}
            className="btn-secondary flex items-center gap-2"
            aria-label="Edit game details"
          >
            <Edit2 size={18} />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center justify-center p-2 rounded-xl border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition"
            aria-label="Delete game"
            title="Delete game"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {showAttendance && currentTeam && (
        <EventAttendancePanel
          eventType="game"
          eventId={game.id}
          eventDate={game.date}
          eventTime={game.game_time}
          teamId={currentTeam.id}
          title="Game Attendance"
          onClose={() => setShowAttendance(false)}
        />
      )}

      {/* Match status + Start Match — Tennis only */}
      {currentTeam?.sport === 'Tennis' && game && (
        <TennisMatchStatusCard game={game} onStarted={refreshGame} />
      )}

      {/* Box Score — Baseball/Softball only */}
      {isBaseballLike && (() => {
        const result = displayScores
          ? gameResult({ our_score: displayScores.ourRuns, opponent_score: displayScores.opponentRuns })
          : (game ? gameResult(game) : null);

        // Compute batting / pitching totals from already-loaded playerStats
        const totals: Record<string, number> = {};
        for (const stats of playerStats.values()) {
          for (const { stat_key, stat_value } of stats) {
            if (Number.isFinite(stat_value)) {
              totals[stat_key] = (totals[stat_key] ?? 0) + stat_value;
            }
          }
        }
        const battingCols = [
          { label: 'AB', key: 'AB' },
          { label: 'R', key: 'R' },
          { label: 'H', key: 'H' },
          { label: '2B', key: '2B' },
          { label: '3B', key: '3B' },
          { label: 'HR', key: 'HR' },
          { label: 'RBI', key: 'RBI' },
          { label: 'BB', key: 'BB' },
          { label: 'K', key: 'K' },
          { label: 'SB', key: 'SB' },
        ].filter(col => totals[col.key] != null);

        const pitchingCols = [
          { label: 'IP', key: 'IP' },
          { label: 'H', key: 'H_Allowed' },
          { label: 'R', key: 'R_Allowed' },
          { label: 'ER', key: 'ER' },
          { label: 'BB', key: 'BB_Allowed' },
          { label: 'K', key: 'K' },
          { label: 'Pitches', key: 'Pitches' },
        ].filter(col => totals[col.key] != null);

        // Personal accounts get an "Add Score" affordance even with zero stats entered,
        // since their one player's stats may never cover the whole game's final score.
        if (!displayScores && battingCols.length === 0 && pitchingCols.length === 0 && !currentTeam?.isPersonal) return null;

        return (
          <div className="bg-white rounded-lg shadow p-3 md:max-w-lg">
            {/* Scoreboard */}
            {(displayScores || currentTeam?.isPersonal) && (
              <div className="mb-3">
                {!displayScores && currentTeam?.isPersonal && !showScoreOverride ? (
                  <button
                    type="button"
                    onClick={handleOpenScoreOverride}
                    className="btn-secondary text-sm px-3 py-2 flex items-center gap-1.5"
                  >
                    <Edit2 size={14} />
                    Add Score
                  </button>
                ) : displayScores && (
                <div className="flex items-stretch border border-gray-200 rounded-md overflow-hidden text-sm">
                  {result && (
                    <div className={`flex items-center justify-center px-3 font-bold text-base border-r border-gray-200 ${
                      result === 'W' ? 'bg-green-50 text-green-700' : result === 'L' ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'
                    }`}>
                      {result}
                    </div>
                  )}
                  <div className="flex flex-1 divide-x divide-gray-200">
                    <div className="flex flex-col items-center justify-center flex-1 py-2 px-3 min-w-0 bg-gray-50">
                      <span className="text-xs text-gray-500 truncate w-full text-center leading-none mb-1">{currentTeam?.name ?? 'Us'}</span>
                      <span className="text-2xl font-bold tabular-nums text-gray-900 leading-none">{displayScores.ourRuns}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center flex-1 py-2 px-3 min-w-0">
                      <span className="text-xs text-gray-500 truncate w-full text-center leading-none mb-1">{game?.opponent_name ?? 'Opponent'}</span>
                      <span className="text-2xl font-bold tabular-nums text-gray-900 leading-none">{displayScores.opponentRuns}</span>
                    </div>
                  </div>
                  {currentTeam?.isPersonal && !showScoreOverride && (
                    <button
                      type="button"
                      onClick={handleOpenScoreOverride}
                      className="flex items-center justify-center px-2 border-l border-gray-200 text-gray-400 hover:text-primary hover:bg-gray-50"
                      aria-label="Edit score"
                      title="Edit score"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
                )}

                {/* Personal accounts only: the stats-derived score only reflects the one
                    player's own runs, not the final score — a manual override fills that gap. */}
                {currentTeam?.isPersonal && game?.manual_score && !showScoreOverride && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Manually set.{' '}
                    <button
                      type="button"
                      onClick={handleUseStatsDerivedScore}
                      disabled={savingScoreOverride}
                      className="text-primary hover:underline disabled:opacity-50"
                    >
                      Use stats-derived score instead
                    </button>
                  </p>
                )}

                {currentTeam?.isPersonal && showScoreOverride && (
                  <form onSubmit={handleSaveScoreOverride} className="mt-2 flex items-end gap-2 text-sm">
                    <div>
                      <label htmlFor="override-our-score" className="block text-xs text-gray-500 mb-0.5">{currentTeam?.name ?? 'Us'}</label>
                      <input
                        id="override-our-score"
                        type="number"
                        min={0}
                        value={overrideOurScore}
                        onChange={(e) => setOverrideOurScore(e.target.value)}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-center tabular-nums focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="override-opponent-score" className="block text-xs text-gray-500 mb-0.5">{game?.opponent_name ?? 'Opponent'}</label>
                      <input
                        id="override-opponent-score"
                        type="number"
                        min={0}
                        value={overrideOpponentScore}
                        onChange={(e) => setOverrideOpponentScore(e.target.value)}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-center tabular-nums focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <button type="submit" disabled={savingScoreOverride} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">
                      {savingScoreOverride ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowScoreOverride(false)}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Batting & Pitching totals */}
            {battingCols.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 px-0.5">Batting</p>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        {battingCols.map((col, i) => (
                          <th key={col.key} className={`text-center font-semibold text-gray-600 py-1 px-2 whitespace-nowrap ${i > 0 ? 'border-l border-gray-200' : ''}`}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        {battingCols.map((col, i) => (
                          <td key={col.key} className={`text-center text-gray-900 py-1.5 px-2 tabular-nums font-medium ${i > 0 ? 'border-l border-gray-200' : ''}`}>{totals[col.key]}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {pitchingCols.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 px-0.5">Pitching</p>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        {pitchingCols.map((col, i) => (
                          <th key={col.key} className={`text-center font-semibold text-gray-600 py-1 px-2 whitespace-nowrap ${i > 0 ? 'border-l border-gray-200' : ''}`}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        {pitchingCols.map((col, i) => (
                          <td key={col.key} className={`text-center text-gray-900 py-1.5 px-2 tabular-nums font-medium ${i > 0 ? 'border-l border-gray-200' : ''}`}>
                            {col.key === 'IP' ? Number(totals[col.key]).toFixed(1) : totals[col.key]}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ================================================================
          Football game detail: score card, tabbed subs/stats/gameplan/notes
          ================================================================ */}
      {isFootball && gameId && (
        <FootballGameDetail
          game={game}
          gameId={gameId}
          currentTeam={currentTeam}
          allTeamPlayers={allTeamPlayers}
          playerCards={playerCards}
          playerStats={playerStats}
          calculatedStats={calculatedStats}
          recordStatsOrderedPlayers={recordStatsOrderedPlayers}
          expandedStatsPlayerIds={expandedStatsPlayerIds}
          hasExistingGameStats={hasExistingGameStats}
          onToggleStatsExpanded={toggleStatsExpanded}
          onEditStats={(playerId, cardId) => {
            setSelectedPlayerCardId(cardId);
            setEditingPlayerId(playerId);
            setShowAddStatsModal(true);
          }}
          refreshGame={refreshGame}
          loadGameStats={loadGameStats}
        />
      )}

      {/* Stats for every non-football sport; Baseball/Softball gets its own
          lineup + fielding view, Tennis gets its own match-participants view,
          everything else gets the generic roster stats list. */}
      {!isFootball && !isTennis && <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {isBaseballLike ? 'Lineup & Statistics' : 'Statistics'}
        </h2>

        {isBaseballLike && game && gameId ? (
          <BaseballGameDetail
            game={game}
            gameId={gameId}
            currentTeam={currentTeam}
            allTeamPlayers={allTeamPlayers}
            setAllTeamPlayers={setAllTeamPlayers}
            playerCards={playerCards}
            playerStats={playerStats}
            calculatedStats={calculatedStats}
            lineup={lineup}
            expandedStatsPlayerIds={expandedStatsPlayerIds}
            hasExistingGameStats={hasExistingGameStats}
            onToggleStatsExpanded={toggleStatsExpanded}
            onEditStats={(playerId, cardId) => {
              setSelectedPlayerCardId(cardId);
              setEditingPlayerId(playerId);
              setShowAddStatsModal(true);
            }}
            refreshGame={refreshGame}
            loadGameStats={loadGameStats}
            loadLineup={loadLineup}
          />
        ) : (
          <GenericSportGameDetail
            players={recordStatsOrderedPlayers}
            lineup={lineup}
            playerCards={playerCards}
            playerStats={playerStats}
            calculatedStats={calculatedStats}
            expandedPlayerIds={expandedStatsPlayerIds}
            sport={currentTeam?.sport}
            onToggleExpand={toggleStatsExpanded}
            onEditStats={(playerId, cardId) => {
              setSelectedPlayerCardId(cardId);
              setEditingPlayerId(playerId);
              setShowAddStatsModal(true);
            }}
          />
        )}
      </div>}

      {isTennis && game && gameId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Match Stats</h2>
          <TennisMatchDetail game={game} gameId={gameId} />
        </div>
      )}

      {/* Edit game details */}
      {showEditGameModal && game && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit game details</h3>
              <button
                type="button"
                onClick={() => !editGameSaving && setShowEditGameModal(false)}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={editGameSaving}
                aria-label="Close edit game"
              >
                <X size={20} />
              </button>
            </div>
            <form
              id="edit-game-details-form"
              onSubmit={saveEditedGame}
              className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4"
            >
              <div>
                <label htmlFor="edit-game-date" className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline w-4 h-4 mr-2" />
                  Date <span className="text-red-500">*</span>
                </label>
                <NativeDateInput
                  id="edit-game-date"
                  required
                  value={editGameForm.date}
                  onChange={(e) => setEditGameForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="edit-game-opponent" className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline w-4 h-4 mr-2" />
                  Opponent <span className="text-red-500">*</span>
                </label>
                <input
                  id="edit-game-opponent"
                  type="text"
                  required
                  value={editGameForm.opponent_name}
                  onChange={(e) => setEditGameForm((f) => ({ ...f, opponent_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Opponent team name"
                />
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  <Home className="inline w-4 h-4 mr-2" />
                  Home / Away
                </span>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="editGameHomeAway"
                      checked={editGameForm.home_away === 'home'}
                      onChange={() => setEditGameForm((f) => ({ ...f, home_away: 'home' }))}
                      className="mr-2"
                    />
                    <Home className="w-4 h-4 mr-1" />
                    Home
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="editGameHomeAway"
                      checked={editGameForm.home_away === 'away'}
                      onChange={() => setEditGameForm((f) => ({ ...f, home_away: 'away' }))}
                      className="mr-2"
                    />
                    <Plane className="w-4 h-4 mr-1" />
                    Away
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="edit-game-location" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline w-4 h-4 mr-2" />
                  Location (optional)
                </label>
                <input
                  id="edit-game-location"
                  type="text"
                  value={editGameForm.location}
                  onChange={(e) => setEditGameForm((f) => ({ ...f, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Venue or field"
                />
              </div>
              <div>
                <label htmlFor="edit-game-time" className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="inline w-4 h-4 mr-2" />
                  Game time (optional)
                </label>
                <NativeTimeInput
                  id="edit-game-time"
                  value={editGameForm.game_time}
                  onChange={(e) => setEditGameForm((f) => ({ ...f, game_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </form>
            <div
              className="flex-shrink-0 border-t border-gray-200 p-4 flex gap-2 bg-white"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
            >
              <button
                type="button"
                onClick={() => setShowEditGameModal(false)}
                disabled={editGameSaving}
                className="flex-1 btn-secondary py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="edit-game-details-form"
                disabled={editGameSaving}
                className="flex-1 btn-primary py-2"
              >
                {editGameSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Stats Modal */}
      {currentTeam && showAddStatsModal && selectedPlayerCardId && (() => {
        const editingPlayer = recordStatsOrderedPlayers.find((p) => playerCards.get(p.id) === selectedPlayerCardId);
        const playerName = editingPlayer ? `${editingPlayer.first_name ?? ''} ${editingPlayer.last_name ?? ''}`.trim() || undefined : undefined;
        const sharedProps = {
          isOpen: showAddStatsModal,
          onClose: () => {
            setShowAddStatsModal(false);
            setSelectedPlayerCardId(null);
            setEditingPlayerId(null);
          },
          playerCardId: selectedPlayerCardId,
          teamId: currentTeam.id,
          sport: currentTeam.sport,
          onStatsAdded: handleStatsAdded,
          gameId,
          existingStats: editingPlayerId ? playerStats.get(editingPlayerId) || [] : undefined,
          playerName,
        };
        return isFootball
          ? <FootballGameStatsModal {...sharedProps} />
          : <AddGameStatsModal {...sharedProps} />;
      })()}

      {/* Delete game confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteGame}
        title="Delete game?"
        message="This will permanently delete the game along with all associated stats, fielding assignments, and lineup data. This cannot be undone."
        confirmLabel="Delete game"
        confirmAriaLabel="confirm-delete-game"
        variant="danger"
        isConfirming={deletingGame}
      />
    </div>
  );
};

